import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

export function SupportAiGuide() {
  useDocsPage('How the AI first line works — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">How the AI first line works</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          How the <em>AI first line</em> works.
        </h1>
        <p className="dp-oneliner">
          The AI answers a customer first, from your knowledge base and your team&rsquo;s guidelines
          only, with its sources shown. If it cannot answer well, it steps back and a person takes
          over. This guide explains when it answers, when it stops, how to switch it off or cap it,
          what counts as a billable resolution, and how to compare it with Intercom&rsquo;s Fin on
          your past conversations.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="answers">What it does</AnchorHeading>
        <p className="dp-section-sub">
          When a customer opens a request or writes a new message, the AI looks for passages in your
          knowledge base that match, and writes a short answer using only those passages and the{' '}
          <Link to="/docs/guides/support-guidelines">guidelines</Link> your team wrote for that
          customer: the tone to take, context the articles leave out, when to ask a question first
          and what to leave to a person. Under the answer, the customer and your agents see the
          articles it used (the title, a link and the section); guidelines are never shown. In the
          inbox its messages carry an AI badge, and the ticket&rsquo;s Details say whether the AI is
          answering, has handed over, or is off.
        </p>
        <p className="dp-section-sub">
          Two messages sent close together produce one reply, not two. The customer does not wait
          for it: the request is saved first and the answer follows.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="audience">Who sees which article</AnchorHeading>
        <p className="dp-section-sub">
          Which articles the AI may use is decided before it writes anything, never by the AI. The
          search is limited to your workspace and to articles open to the customer&rsquo;s roles and
          plan. Their roles are worked out when the AI answers, from the role value and attributes
          on their profile and your roles&rsquo; current rules, so a change to a role applies at
          once; a customer can have several. The plan is the one on their profile. A customer who
          matches no role only gets articles open to everyone. Setting these up is covered in{' '}
          <Link to="/docs/guides/support-knowledge#audience">Build the knowledge base</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="when">When it answers and when it stays out</AnchorHeading>
        <p className="dp-section-sub">The AI answers a ticket only when all of these hold:</p>
        <ul className="dp-connects">
          <li>
            The AI switch is on for the workspace, for the ticket&rsquo;s type, and for every role
            the customer holds (a role can be set to send its people straight to your team).
          </li>
          <li>The ticket is not closed and has no assignee.</li>
          <li>No agent has replied to the customer in public.</li>
          <li>The AI has not already handed this ticket over.</li>
          <li>It has answered fewer than six times on this ticket.</li>
          <li>The day&rsquo;s AI spending cap, if you set one, has not been reached.</li>
        </ul>
        <p className="dp-section-sub">
          As soon as an agent takes the ticket or replies, the AI stops for that ticket, so it never
          talks over a person.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="handover">Handover</AnchorHeading>
        <p className="dp-section-sub">
          The AI says nothing rather than guess. It hands over in these cases:
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Nothing answers it</div>
            <div className="dp-field-v">
              No article the customer may read matches the question and no guideline covers it, or
              the AI judged they do not answer it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A guideline says so</div>
            <div className="dp-field-v">
              A handover guideline names the question as one for your team. See{' '}
              <Link to="/docs/guides/support-guidelines#types">Write guidelines</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Not backed by a source</div>
            <div className="dp-field-v">A draft that cites nothing is never shown.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">It could not run</div>
            <div className="dp-field-v">The search or the AI failed.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Spending cap reached</div>
            <div className="dp-field-v">The day&rsquo;s AI spend hit your cap.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Six replies already</div>
            <div className="dp-field-v">The most the AI may send on one ticket.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">The customer asks</div>
            <div className="dp-field-v">
              The customer can ask for a person at any time, or mark an answer Not helpful.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          When it hands over, three things happen once. The customer is told, in the same words
          whatever the reason: &ldquo;I could not answer this from our help articles. A person on
          the team will pick it up and reply here.&rdquo; An internal note records the reason for
          your agents. The ticket&rsquo;s history gets a handover event and its AI status becomes
          Handed to the team.
        </p>
        <p className="dp-section-sub">
          <strong>Handover is sticky.</strong> The AI does not answer that ticket again, even if the
          customer writes again, unless a person switches it on for that ticket (see below). Nobody
          is assigned automatically: the ticket waits in the Unassigned view like any new ticket,
          and agents are not yet notified of a handover, so check that view.
        </p>
        <p className="dp-section-sub">
          <strong>Keep the AI answering beside your team.</strong> Normally the AI stays out of a
          ticket once someone is assigned, an agent has replied in public, or it has handed over.
          For a chat where you want both, turn on <strong>AI replies too</strong> in the
          ticket&rsquo;s header (it is not shown on a closed ticket). The AI then keeps answering
          the customer&rsquo;s messages on that ticket. Everything else that stops it still applies:
          a ticket type with the AI off, the daily spending cap, six replies on one ticket, and
          answers it cannot ground in your articles. Turning the switch on or off is recorded in the
          ticket&rsquo;s history as an internal event; the customer never sees it. It is off by
          default on every ticket.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="guardrails">Guardrails</AnchorHeading>
        <ul className="dp-connects">
          <li>
            It answers only from the articles and guidelines it is given. If they do not cover the
            question it says nothing and hands over; it does not fill the gap from general
            knowledge.
          </li>
          <li>
            Articles and customer messages are treated as data, never as instructions; only the
            guidelines your team writes are followed as instructions. A message asking it to change
            its rules or reveal them is ignored.
          </li>
          <li>
            It cannot see or change anyone&rsquo;s account and cannot take actions. For an
            account-specific problem it says a person will need to look.
          </li>
          <li>
            Answers are short and plain, with no greeting or sign-off, in the customer&rsquo;s
            language.
          </li>
          <li>Every answer carries its sources, and one with none is not sent.</li>
          <li>
            Role and plan limits are applied by the search, so an article a customer may not read is
            never given to the AI for that customer.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <AnchorHeading id="switches">Switches and the spending cap</AnchorHeading>
        <UiPath
          steps={['Org Settings', 'Support', 'General', 'AI answers first']}
          note="Admins and owners. On by default for the workspace."
        />
        <UiPath
          steps={[
            'Org Settings',
            'Support',
            'Ticket types & workflows',
            'Edit a type',
            'AI answers this type first',
          ]}
          note="Per type. The list shows each type's setting in the AI answering column. Turn it off for types such as an issue report where you always want a person first."
        />
        <UiPath
          steps={['Org Settings', 'Support', 'General', 'Limit daily AI spend']}
          note="Admins and owners. Enter USD per day, from 0 to 100,000."
        />
        <p className="dp-section-sub">
          With the workspace switch off, the AI neither answers nor writes titles and summaries. The
          spending cap counts everything the AI costs, answers, titles and summaries together, per
          UTC day. When it is reached, new tickets go straight to agents until midnight UTC. There
          is no cap until you turn one on.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="billable">What counts as a billable resolution</AnchorHeading>
        <p className="dp-section-sub">
          Only a resolution the AI achieved on its own is billable, and there are exactly two ways
          one is recorded:
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Confirmed</div>
            <div className="dp-field-v">
              The customer marks an AI answer as solving it. The ticket closes as Solved by AI. This
              only works while the AI still holds the ticket: not after a person replied, the ticket
              was assigned, the AI handed over or the ticket closed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Assumed</div>
            <div className="dp-field-v">
              The AI answered, no person replied in public, nobody is assigned, the AI did not hand
              over, the customer did not rate the answer, and the customer wrote nothing for 24
              hours after it. A check runs every five minutes. It records a charge; it does not
              close the ticket.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          <strong>Never billable:</strong> a ticket the customer closed themselves, one an agent
          solved, Duplicate and Won&rsquo;t fix closes, a ticket the AI handed over, an answer rated
          Not helpful, a ticket with no AI answer, and anything in the sandbox. An agent can never
          set the AI resolution: the Close ticket dialog offers only Solved by agent, Duplicate and
          Won&rsquo;t fix.
        </p>
        <Figure
          title="After the AI answers"
          caption="Only the two paths where the AI held the ticket to the end are charged, at 0.35 USD by default. A handover or a reply after a charge was recorded does not reverse it."
        >
          <Flow
            steps={[
              { title: 'The AI answers', text: 'From your articles, with its sources shown.' },
              {
                fork: [
                  {
                    title: 'Customer confirms',
                    text: 'Closes as Solved by AI. Charged.',
                    tone: 'accent',
                  },
                  {
                    title: 'Silent for 24 hours',
                    text: 'Assumed solved. A charge is recorded and the ticket stays open.',
                    tone: 'accent',
                  },
                  {
                    title: 'Not helpful, or asks for a person',
                    text: 'Handed over to the team. Not charged, unless a charge was already recorded.',
                    tone: 'warn',
                  },
                  {
                    title: 'A person replies',
                    text: 'The AI stops. Not charged, unless a charge was already recorded.',
                    tone: 'muted',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <p className="dp-section-sub">
          A ticket is charged once, ever. A charge can be reversed by an admin through the admin
          API, which adds a reversal and leaves the original on record, and the ticket cannot be
          charged again. There is no button for this in the dashboard yet, and nothing reverses on
          its own, not even when a customer reopens the ticket or asks for a person afterwards.
          Whether that should happen automatically is still being decided.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="usage">Read the AI usage figures</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'AI resolutions this month']}
          note="Admins and owners only. It shows the current UTC month. There is no separate AI usage settings page: the switches are under Org Settings, Support, General."
        />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Billable</div>
            <div className="dp-field-v">Confirmed plus assumed, minus reversed.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Confirmed by the customer</div>
            <div className="dp-field-v">Resolutions the customer marked as solved.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Assumed after 24 hours</div>
            <div className="dp-field-v">Resolutions recorded after a day of silence.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Reversed</div>
            <div className="dp-field-v">Charges that were reversed this month.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Amount</div>
            <div className="dp-field-v">
              Billable resolutions at the price per resolution, in US dollars. The price is set for
              the platform and defaults to 0.35 USD.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">AI cost</div>
            <div className="dp-field-v">
              What the AI cost to run this month on real tickets: answers, titles and summaries,
              whether or not they ended in a billable resolution. Sandbox use is not included. The
              spending cap reads the same figures by day.
            </div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            These figures are a tally inside Support. They are not yet rolled up into a usage line
            on your Revvly subscription or invoice, because that connection has not been made. Do
            not expect them on an invoice yet.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="titles">Automatic titles and close summaries</AnchorHeading>
        <p className="dp-section-sub">
          <strong>Titles.</strong> A ticket opened without a title gets one of at most eight words,
          written from the customer&rsquo;s first message. If a person names the ticket in the
          meantime, theirs wins. You can edit any title.
        </p>
        <p className="dp-section-sub">
          <strong>Close summaries.</strong> Shortly after a ticket closes the AI writes two or three
          sentences from the public conversation and how it ended. It shows as Close summary at the
          top of a closed ticket and under the ticket on the client&rsquo;s Support tab, and only
          agents see it. The summary is also kept in a searchable history for agents; it is never
          used to answer a customer. A panel that finds similar past tickets has not been built yet.
        </p>
        <p className="dp-section-sub">
          Both follow the AI switch and the spending cap, and neither can ever be a billable
          resolution.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="sandbox">Try it in the sandbox</AnchorHeading>
        <UiPath
          steps={['Support', 'Inbox', 'Sandbox']}
          note="Linked at the foot of the views column. Any agent can open it. Needs no secret key and touches no real customer."
        />
        <p className="dp-section-sub">
          The first time you open it, Revvly makes a hidden practice copy of your Support setup:
          your ticket types, workflows, roles, auto-close, AI and spending settings. Choose a
          practice customer (a name, optionally an email and company, a role value, attributes and a
          plan, which your role rules turn into roles exactly as for a real customer) and write to
          support as them on one side, while the real inbox screens on the other side work the same
          ticket. Saved customers are remembered in your browser.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Real knowledge</div>
            <div className="dp-field-v">
              The AI answers from your real workspace&rsquo;s knowledge base with the same audience
              rules, so what a practice Founder is told is what a real Founder would be told.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Never billable</div>
            <div className="dp-field-v">
              Sandbox answers are recorded as practice, are never charged, and share a small daily
              cap of 2 USD of their own.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Nothing leaves</div>
            <div className="dp-field-v">
              Practice tickets are numbered SBX-1, SBX-2 and so on. Nothing reaches your real inbox,
              client pages, metrics or email, and no email is sent.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Reset sandbox</div>
            <div className="dp-field-v">
              Admins and owners can delete every practice ticket and customer and copy the setup
              again, for example after changing roles or types.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="compare">Compare with Intercom</AnchorHeading>
        <UiPath
          steps={['Support', 'Compare with Intercom']}
          note="Admins and owners. Open it from the address /support/compare, or from the link under a completed conversation import (Support, Knowledge, Import, Past conversations, or Org Settings, Support, Import)."
        />
        <p className="dp-section-sub">
          If you are moving from Intercom, you can see what the AI would have done on your past
          conversations before you switch it on, and set that beside what Intercom&rsquo;s AI agent,
          Fin, actually did. It needs your Intercom conversations imported (Import, Past
          conversations, on the Knowledge page or at the foot of the inbox views column) and a
          knowledge base for the AI to answer from. The AI switch does not need to be on: nothing is
          sent to anyone.
        </p>
        <p className="dp-section-sub">
          Choose a date range and how many conversations to sample (200 by default, at most 500).
          Before you start, Revvly shows how many imported conversations the range holds and what
          the run should cost. For each sampled conversation, the AI answers the customer&rsquo;s
          question exactly as it would answer a new ticket: from your knowledge base, limited to the
          articles that customer&rsquo;s roles and plan allow, and with the guidelines that customer
          would get today. A second AI model then grades the answer against the reply the customer
          actually received, reading the same guidelines so an answer that rests on one is judged
          with its source.
        </p>
        <p className="dp-section-sub">
          The question is the customer&rsquo;s first real one. A thin opener, such as a greeting or
          one of Intercom&rsquo;s quick-reply buttons (a message with no question mark and fewer
          than six words), is skipped in favour of the next message from the customer that says
          something, looking at their first few messages before a person replied. The opener still
          goes in as context. Each customer&rsquo;s roles are worked out from their Intercom role
          and attributes, with the same rules as a live customer (see{' '}
          <Link to="/docs/guides/support-setup#roles">Roles</Link>). When a customer matches none of
          your roles and nothing open to everyone answers, the conversation is marked{' '}
          <strong>No matching role</strong> and left out of the rates, so a gap in your role setup
          is not counted against the AI.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Would resolve</div>
            <div className="dp-field-v">
              Correct and complete enough that the customer would not have needed to write again.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Partial</div>
            <div className="dp-field-v">Correct as far as it goes, but missing something.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Incorrect</div>
            <div className="dp-field-v">Contradicts the reply the customer got, or misleads.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Handed over</div>
            <div className="dp-field-v">
              The AI declined to answer, so a person would have taken it.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          The report ends in two figures, each with a 95% range and the number of conversations it
          rests on. Each Fin figure sits beside the Revvly figure counted the same way, so they can
          be read as a pair. <strong>Fin, answered</strong> is the share of the conversations Fin
          took part in that Intercom recorded as resolved (assumed or confirmed), and{' '}
          <strong>Revvly, answered</strong> is the share of those same conversations where Revvly
          gave an answer rather than handing over, whether or not it was complete.{' '}
          <strong>Fin, confirmed</strong> is the confirmed-only share, beside{' '}
          <strong>Revvly, would resolve</strong>, the share whose answer would have resolved it.{' '}
          <strong>Revvly, all graded</strong> covers every graded conversation, including ones Fin
          never took part in, which are mostly account problems a person handled. A head-to-head
          shows, on the conversations Fin took part in, where both, only one, or neither would have
          resolved it, and the table lists every conversation with the question, Fin&rsquo;s outcome
          and sources, the AI&rsquo;s answer and sources, the reply the customer got, and why it was
          graded as it was. With fewer than 50 graded conversations the page says the two figures
          cannot be compared yet.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Read the figures with care. The AI answers from your knowledge base as it is today, not
            as it was when each conversation happened. The grading is done by an AI model, not a
            person. And Fin&rsquo;s &ldquo;assumed resolution&rdquo; only means the customer did not
            come back, not that the answer worked.
          </span>
        </div>
        <p className="dp-section-sub">
          <strong>Why conversations were not resolved</strong> sits above the table. It reads the
          articles Fin cited beside what Revvly&rsquo;s search returned, and gives each conversation
          that was not resolved its most likely reason, closest miss first, with a count, a hint on
          what to do and a <strong>Show</strong> button that filters the table to those
          conversations:
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Found Fin&rsquo;s article, still fell short</div>
            <div className="dp-field-v">
              The search returned the article Fin used and Revvly still did not resolve. Compare the
              article&rsquo;s wording with the question, and read the grader&rsquo;s reason.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fin&rsquo;s article not found by the search</div>
            <div className="dp-field-v">
              The customer could read the article, and the search did not bring it up. Add the
              phrasing customers use, or a short question-and-answer section.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fin&rsquo;s article hidden from this customer</div>
            <div className="dp-field-v">
              The article is searchable, but its audience leaves this customer out. Check the
              article&rsquo;s roles, or the rules that give this customer a role.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fin&rsquo;s article not searchable</div>
            <div className="dp-field-v">
              It is in your knowledge base but left out of search: excluded, failed, or waiting for
              review. Review it under Knowledge.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fin&rsquo;s article not in your knowledge base</div>
            <div className="dp-field-v">
              No article matches the one Fin used. Import it from Knowledge.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fin cited nothing</div>
            <div className="dp-field-v">
              There is no telling which article was needed. Read the reply the customer received.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          The table has a <strong>Reason</strong> filter and a <strong>Likely reason</strong>{' '}
          column. A <strong>Show</strong> filter beside the verdict and reason picks out{' '}
          <strong>Fin resolved, Revvly didn&rsquo;t</strong> (Fin assumed or confirmed a resolution
          and Revvly&rsquo;s grade was partial, incorrect, handed over or no matching role) or{' '}
          <strong>Fin found an article, Revvly&rsquo;s search didn&rsquo;t</strong> (Fin cited an
          article and Revvly&rsquo;s search returned none of its sources). Paging and the CSV
          download follow the filters you have set. Open a conversation to see, beside the grade,
          its likely reason, the sources Fin cited (each marked as in your knowledge base, hidden
          from this customer, not searchable or missing, and whether Revvly&rsquo;s search found
          it), the articles Revvly&rsquo;s search returned with their scores, the text it searched
          for, and the <strong>Guidelines Revvly followed</strong>, each marked when the answer
          cited it. A comparison run before Revvly recorded what it searched shows no reasons; start
          a new comparison to see them.
        </p>
        <p className="dp-section-sub">
          When no article answers the exact question but one covers the same feature or task, the AI
          answers from it and says what the article does not cover; it hands over only when nothing
          bears on the topic. When a question could mean a few things your articles answer
          differently, it answers each reading briefly in one reply instead of asking which was
          meant, and asks only when that will not fit or a missing detail leaves no useful answer.
        </p>
        <p className="dp-section-sub">
          <strong>Comparison history.</strong> Every run is kept. The Comparison history table under
          the report lists them all with the Revvly and Fin rates, the change in points since the
          run before, the cost, the models, and a CSV download of that run&rsquo;s conversations.
          Select a run to open its full report in place of the latest one; Back to the latest
          returns.
        </p>
        <p className="dp-section-sub">
          The run spends AI and shows in the AI cost, and it can never be a billable resolution. It
          is held to your daily spending cap, but counted apart from live answers: the cap bounds
          what comparison runs spend in a day, and live AI answers are not affected by it. If a run
          reaches the cap it stops with the results so far, and you can resume it later from where
          it stopped. Each run also records a fingerprint of the knowledge base it answered from,
          and is flagged if the knowledge changed while it was paused, because its results then mix
          two versions. One comparison runs at a time per workspace.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="not-built">Not built yet</AnchorHeading>
        <ul className="dp-connects">
          <li>Notifying agents when the AI hands over a ticket.</li>
          <li>The usage line on your Revvly subscription.</li>
          <li>Images in AI answers.</li>
          <li>A box to test what a given role and plan would retrieve.</li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-knowledge">
              Build the knowledge base the AI answers from ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/support-guidelines">Write guidelines for the support AI ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-inbox">Work the support inbox ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
